/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, Dec 2021
 * @flow
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import { storeProductionEnum } from '../../../../enums';
import constants from '../../../../globalization';
import blackCloseIcon from '../../../../assets/images/Fireboard/menu_buttons/black-close.png';
import blackEditIcon from '../../../../assets/images/Fireboard/menu_buttons/black-edit.png';
import backImg from '../../../../assets/images/Fireboard/back2_red.png';
import classes from './ProductionRate.module.css';

const ProductionRate = props => {
  const { productionRateList, onClose, onManageOverrides } = props;

  const [selectedList, setSelectedList] = useState(new Map());

  const onSelectRow = (row) => {
    let copiedList = new Map(selectedList);
    if (copiedList.has(row.id)) { //if already present in list - remove entry
      copiedList.delete(row.id);
    } else { //if already not present in list - add entry
      copiedList.set(row.id, row);
    }
    setSelectedList(copiedList);
  };

  const onBulkEdit = () => {
    const ids = Array.from(selectedList.keys());
    const production = selectedList.get(ids[0]);
    onManageOverrides(storeProductionEnum.BULKEDIT, production, ids);
  };

  return (
    <div className={classes.container}>
      <div className={classes.headingWrapper}>
        <div className={classes.leftheaderWrapper}>
          <img src={backImg} className={classes.backImage} alt="Back" onClick={onClose} />
          <span className={classes.title}>{constants.productionRateForToday}</span>
        </div>
        <div className={classes.rightheaderWrapper}>
          <Button
            className={classes.bulkEditBtn}
            onClick={onBulkEdit}
            disabled={selectedList.size === 0}>
            <span className={classes.bulkEditBtnTxt}>{constants.bulkEdit}</span>
          </Button>
        </div>
      </div>
      <div className={classes.bodyWrapper}>
        <table className={classes.table}>
          <TableHeader />
          <TableBody
            productionRateList={productionRateList}
            onManageOverrides={onManageOverrides}
            selectedList={selectedList}
            onSelectRow={onSelectRow}
          />
        </table>
      </div>
    </div>
  );
};

export default ProductionRate;

ProductionRate.propTypes = {
  productionRateList: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageOverrides: PropTypes.func.isRequired,
};

ProductionRate.defaultProps = {
  productionRateList: [],
  onClose: () => { },
  onManageOverrides: () => { },
};

const TableHeader = () => {
  return (
    <thead>
      <tr>
        <th>{constants.idCaps}</th>
        <th>{constants.startTime}</th>
        <th>{constants.endTime}</th>
        <th>{constants.prepWindow}</th>
        <th>{constants.production}</th>
        <th>{constants.overridePrepWindow}</th>
        <th>{constants.overrideProduction}</th>
        <th>{constants.manageOverrides}</th>
        <th>{constants.bulkEdit}</th>
      </tr>
    </thead>
  );
};

const TableBody = props => {
  const { productionRateList, onManageOverrides, selectedList, onSelectRow } = props;

  const areOverrideColumnsNull = (production) => {
    return (!production.overridePrepWindow && !production.overrideProduction);
  };

  const getAction = (production) => {
    const areColumnsNull = areOverrideColumnsNull(production);
    const buttonText = areColumnsNull ? constants.editLabel : constants.clearLabel;
    const buttonIcon = areColumnsNull ? blackEditIcon : blackCloseIcon;
    return (
      <Button
        className={classes.editButton}
        onClick={() => onManageOverrides(areColumnsNull ? storeProductionEnum.EDIT : storeProductionEnum.CLEAR, production)}
        disabled={(selectedList && selectedList.size !== 0)}>
        <div className={classes.btnContentWrapper}>
          <img src={buttonIcon} className={classes.buttonImg} />
          <span className={classes.editButtonText}>{buttonText}</span>
        </div>
      </Button>
    );
  };

  return (
    productionRateList.map(eachProduction => (
      <tbody key={`id-${eachProduction.id}`}>
        <tr>
          <td>{eachProduction?.id}</td>
          <td>{eachProduction?.startTime}</td>
          <td>{eachProduction?.endTime}</td>
          <td>{eachProduction?.prepWindow}</td>
          <td>{eachProduction?.production}</td>
          <td>{eachProduction?.overridePrepWindow}</td>
          <td>{eachProduction?.overrideProduction}</td>
          <td>{getAction(eachProduction)}</td>
          <td>
            <Checkbox checked={selectedList.has(eachProduction.id)} onChange={() => onSelectRow(eachProduction)} />
          </td>
        </tr>
      </tbody>
    ))
  );
};

TableBody.propTypes = {
  productionRateList: PropTypes.array.isRequired,
  onManageOverrides: PropTypes.func.isRequired,
  selectedList: PropTypes.instanceOf(Map),
  onSelectRow: PropTypes.func,
};

TableBody.defaultProps = {
  productionRateList: [],
  onManageOverrides: () => { },
  selectedList: new Map(),
  onSelectRow: () => { },
};
